import React from 'react';
import { WheelSafeNumberInput } from '../../common/WheelSafeNumberInput';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  FunctionField,
  ReferenceField,
  Filter,
  TextInput,
  SelectInput,
  DateInput,
  useListContext,
  TopToolbar,
  ExportButton,
  CreateButton,
  ShowButton,
  useNotify,
  useRefresh,
  useDataProvider,
  BulkDeleteButton,
  useTranslate,
  RecordContextProvider
} from 'react-admin';
import {
  Chip,
  Box,
  Typography,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Person
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../../config/api';
import { tokenManager } from '../../../utils/tokenManager';

const TransactionFilter = (props: any) => {
  const translate = useTranslate();

  return (
    <Filter {...props}>
      <TextInput label={translate('resources.app-transactions.filters.search')} source="search" alwaysOn />
      <SelectInput
        label={translate('resources.app-transactions.filters.type')}
        source="type"
        choices={[
          // Top-ups are excluded from this list — they live on the dedicated
          // /app-topups page so they aren't duplicated here. The backend enforces
          // the same exclusion (`type !== topup` when no explicit type filter).
          { id: 'all', name: translate('resources.app-transactions.types.all') },
          { id: 'deduction', name: translate('resources.app-transactions.types.deduction') },
          { id: 'pre_auth', name: translate('resources.app-transactions.types.pre_auth') },
          { id: 'refund', name: translate('resources.app-transactions.types.refund') },
          { id: 'reward', name: translate('resources.app-transactions.types.reward') },
          { id: 'adjustment', name: translate('resources.app-transactions.types.adjustment') }
        ]}
      />
      <SelectInput
        label={translate('resources.app-transactions.filters.status')}
        source="status"
        choices={[
          { id: 'all', name: translate('resources.app-transactions.statuses.all') },
          { id: 'pending', name: translate('resources.app-transactions.statuses.pending') },
          { id: 'completed', name: translate('resources.app-transactions.statuses.completed') },
          { id: 'failed', name: translate('resources.app-transactions.statuses.failed') },
          { id: 'cancelled', name: translate('resources.app-transactions.statuses.cancelled') }
        ]}
      />
      <DateInput label={translate('resources.app-transactions.filters.from_date')} source="from_date" />
      <DateInput label={translate('resources.app-transactions.filters.to_date')} source="to_date" />
      <WheelSafeNumberInput label={translate('resources.app-transactions.filters.min_amount')} source="min_amount" />
      <WheelSafeNumberInput label={translate('resources.app-transactions.filters.max_amount')} source="max_amount" />
    </Filter>
  );
};

const TypeChip = ({ type }: { type: string }) => {
  const translate = useTranslate();

  const getColor = () => {
    switch (type) {
      case 'topup': return 'success';
      case 'deduction': return 'warning';
      case 'refund': return 'info';
      case 'pre_auth': return 'default';
      case 'reward': return 'secondary';
      default: return 'default';
    }
  };

  const getLabel = () => {
    return translate(`resources.app-transactions.types.${type}`, { _: type });
  };

  return (
    <Chip
      label={getLabel()}
      color={getColor() as any}
      size="small"
    />
  );
};

const StatusChip = ({ status }: { status: string }) => {
  const translate = useTranslate();

  const getColor = () => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'hold': return 'default';
      case 'released': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  return (
    <Chip
      label={translate(`resources.app-transactions.statuses.${status}`, { _: status })}
      color={getColor() as any}
      size="small"
    />
  );
};

const PaymentMethodField = ({ method }: { method?: string }) => {
  const translate = useTranslate();

  if (!method) return <Typography variant="body2">-</Typography>;

  return (
    <Typography variant="body2">
      {translate(`resources.app-transactions.paymentMethods.${method}`, { _: method })}
    </Typography>
  );
};

const DesktopListActions = () => {
  const { total } = useListContext();
  const refresh = useRefresh();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const translate = useTranslate();

  const handleExport = async () => {
    try {
      const token = await tokenManager.getAccessToken();
      const response = await fetch(`${API_CONFIG.baseURL}/api/backend/app-transactions/export`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `app-transactions-${format(new Date(), 'yyyyMMdd-HHmmss')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      notify(translate('resources.app-transactions.messages.exportSuccess'), { type: 'success' });
    } catch (error) {
      notify(translate('resources.app-transactions.messages.exportFailed'), { type: 'error' });
    }
  };

  return (
    <TopToolbar>
      <Button
        startIcon={<RefreshIcon />}
        onClick={refresh}
        variant="outlined"
        size="small"
        sx={{ mr: 1 }}
      >
        {translate('resources.app-transactions.actions.refresh')}
      </Button>
      <Button
        startIcon={<DownloadIcon />}
        onClick={handleExport}
        variant="contained"
        size="small"
      >
        {translate('resources.app-transactions.actions.export')}
      </Button>
    </TopToolbar>
  );
};

const MobileListActions = () => {
  const refresh = useRefresh();
  const notify = useNotify();
  const translate = useTranslate();

  const handleExport = async () => {
    try {
      const token = await tokenManager.getAccessToken();
      const response = await fetch(`${API_CONFIG.baseURL}/api/backend/app-transactions/export`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `app-transactions-${format(new Date(), 'yyyyMMdd-HHmmss')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      notify(translate('resources.app-transactions.messages.exportSuccess'), { type: 'success' });
    } catch (error) {
      notify(translate('resources.app-transactions.messages.exportFailed'), { type: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, p: 0 }}>
      <Button
        startIcon={<RefreshIcon />}
        onClick={refresh}
        size="small"
        sx={{ minWidth: 'auto' }}
      >
        {translate('resources.app-transactions.actions.refresh')}
      </Button>
      <Button
        startIcon={<DownloadIcon />}
        onClick={handleExport}
        size="small"
        sx={{ minWidth: 'auto' }}
      >
        {translate('resources.app-transactions.actions.export')}
      </Button>
    </Box>
  );
};

const MobileList = () => {
  const { data, isLoading } = useListContext();
  const navigate = useNavigate();
  const translate = useTranslate();
  if (isLoading || !data) return null;
  return (
    <Box>
      {data.map((record: any) => (
        <RecordContextProvider key={record.id} value={record}>
          <Box
            onClick={() => navigate(`/app-transactions/${record.id}/show`)}
            sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: (record.type === 'topup' || record.type === 'refund') ? 'success.main' : 'text.primary',
                }}>
                  {(record.type === 'topup' || record.type === 'refund') ? '+' : '-'} HKD {parseFloat(record.amount).toFixed(2)}
                </Typography>
                <TypeChip type={record.type} />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <StatusChip status={record.status} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {record.user?.name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Person sx={{ fontSize: 14, color: '#999' }} />
                  <Typography variant="caption" color="text.secondary">
                    {record.user.name}
                  </Typography>
                </Box>
              )}
              {record.payment_method && (
                <Typography variant="caption" color="text.secondary">
                  {translate(`resources.app-transactions.paymentMethods.${record.payment_method}`, { _: record.payment_method })}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {record.timestamp ? new Date(record.timestamp).toLocaleDateString() : ''}
              </Typography>
            </Box>
          </Box>
        </RecordContextProvider>
      ))}
    </Box>
  );
};

export const AppTransactionsList = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <List
      filters={<TransactionFilter />}
      actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
      empty={isMobile ? false : undefined}
      perPage={25}
      sort={{ field: 'timestamp', order: 'DESC' }}
      title={translate('resources.app-transactions.name')}
      sx={{
        '& .RaList-content': {
          backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
        },
        ...(isMobile && {
          '& .RaList-main': { margin: 0 },
          '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
          '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
          '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
          '& .RaListToolbar-root > span': { display: 'none' },
          '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
          '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
          '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
          '& .filter-field .MuiFormControl-root': { width: '100% !important' },
          '& .filter-field .MuiInputBase-root': { width: '100% !important' },
          '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
        }),
      }}
    >
      {isMobile ? (
        <MobileList />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          sx={{
            '& .RaDatagrid-headerCell': {
              backgroundColor: '#F5F5F5',
              fontWeight: 'bold',
            },
            '& .RaDatagrid-row': {
              '&:hover': {
                backgroundColor: '#F0F7FF',
              },
            },
          }}
        >
          <TextField source="id" label={translate('resources.app-transactions.fields.id')} />
          <DateField
            source="timestamp"
            label={translate('resources.app-transactions.fields.timestamp')}
            showTime
          />
          <FunctionField
            label={translate('resources.app-transactions.fields.user.name')}
            render={(record: any) => (
              <Box>
                <Typography variant="body2">{record.user?.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {record.user?.email}
                </Typography>
              </Box>
            )}
          />
          <FunctionField
            label={translate('resources.app-transactions.fields.type')}
            render={(record: any) => <TypeChip type={record.type} />}
          />
          <FunctionField
            label={translate('resources.app-transactions.fields.amount')}
            render={(record: any) => {
              const isCredit = record.type === 'topup' || record.type === 'refund';
              return (
                <Typography
                  variant="body2"
                  color={isCredit ? 'success.main' : 'text.primary'}
                  fontWeight="bold"
                >
                  {isCredit ? '+' : '-'} HKD {parseFloat(record.amount).toFixed(2)}
                </Typography>
              );
            }}
          />
          <FunctionField
            label={translate('resources.app-transactions.fields.payment_method')}
            render={(record: any) => <PaymentMethodField method={record.payment_method} />}
          />
          <FunctionField
            label={translate('resources.app-transactions.fields.status')}
            render={(record: any) => <StatusChip status={record.status} />}
          />
          <ShowButton label="" />
        </Datagrid>
      )}
    </List>
  );
};

export default AppTransactionsList;
